import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./LoyaltyDirectory.css";
import LoadingPage from "../../components/Loading/LoadingPage";
import EventsCarousel from "./EventsCarousel";
import HowItWorksSection from "./HowItWorksSection";
import VenuesList from "./VenuesList";
import { useAuth } from "../../contexts/AuthContext";
import { useHeader } from "../../contexts/HeaderContext";
import { initializeWithCache, setCachedData, hasValidCache } from "../../utils/cacheUtils";
import {
  fetchEnrolledVenues,
  fetchAllVenueOffers,
  checkAllEnrollments,
  fetchCouponCounts,
  fetchTrendingEvents,
  fetchVenueDistances,
  fetchCategoryIcons,
  getCustomerToken,
} from "../../services/loyaltyService";
import { getEventTiers, getLowestDisplayPrice } from "../../services/ticketingService";

function LoyaltyDirectory() {
  const navigate = useNavigate();
  const location = useLocation();
  const { code } = useParams();
  const { t } = useTranslation();

  // Use centralized auth
  const { viewMode, email, customerToken, setShowLoading } = useAuth();

  // Use header context to pass data to header
  const { updateHeaderData } = useHeader();

  const authStateRef = useRef({ email, viewMode, customerToken });
  useEffect(() => {
    authStateRef.current = { email, viewMode, customerToken };
  }, [email, viewMode, customerToken]);

  const isFetchingOffers = useRef(false);
  const isFetchingEnrollments = useRef(false);
  const hasFetchedDistances = useRef(false);

  // State with cache initialization
  const [companies, setCompanies] = useState(() => initializeWithCache("bc_companies", []));
  const [venueOffers, setVenueOffers] = useState(() => initializeWithCache("bc_venue_offers", {}));
  const [categoryOrder, setCategoryOrder] = useState(() => initializeWithCache("bc_category_order", []));
  const [couponCounts, setCouponCounts] = useState({});
  const [trendingEvents, setTrendingEvents] = useState(() => initializeWithCache("bc_trending_events", []));
  const [eventPrices, setEventPrices] = useState(() => initializeWithCache("bc_event_prices", {}));

  const [loading, setLoading] = useState(true);
  const [checkingEnrollments, setCheckingEnrollments] = useState(false);
  const [enrollmentStatus, setEnrollmentStatus] = useState({});
  const [loadingEnrollment, setLoadingEnrollment] = useState({});
  const [isAuthChanging, setIsAuthChanging] = useState(false);

  const [pendingViewDealsVenueId, setPendingViewDealsVenueId] = useState(null);

  // Initialize selectedCategory from navigation state if present (for category navigation from other pages)
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return location.state?.selectedCategory || "All";
  });

  // User location + cached distances
  const [userLocation, setUserLocation] = useState(() => initializeWithCache("bc_user_location", null));
  const [venueDistances, setVenueDistances] = useState(() => initializeWithCache("bc_venue_distances", {}));

  // Lock in the category display order to prevent reordering
  const finalizedCategoryOrder = useRef(null);

  // Hydration and initial data readiness
  const [initialDataReady, setInitialDataReady] = useState(false);
  const [hasHydrated, setHasHydrated] = useState(false);
  const [sortingComplete, setSortingComplete] = useState(false);
  const [isReturningFromQuestionnaire, setIsReturningFromQuestionnaire] =
    useState(false);

  // NEW: snapshot of the auth state the UI was hydrated for
  const [authSnapshot, setAuthSnapshot] = useState({
    email,
    viewMode,
    customerToken,
  });

  const authChangedSinceSnapshot =
    authSnapshot.email !== email ||
    authSnapshot.viewMode !== viewMode ||
    authSnapshot.customerToken !== customerToken;

  // Store signup code from URL - persist throughout session
  // Exclude "become-partner" which is not a valid company code
  useEffect(() => {
    if (code && code !== "become-partner") {
      sessionStorage.setItem("company_signup_code", code);
    }
  }, [code]);

  // Handle category selection from navigation state (when navigating from other pages)
  useEffect(() => {
    if (location.state?.selectedCategory) {
      setSelectedCategory(location.state.selectedCategory);
      // Clear the navigation state after using it to prevent re-applying on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.selectedCategory]);

  // Reset finalized category order when "All" is selected to show all categories
  useEffect(() => {
    if (selectedCategory === "All") {
      finalizedCategoryOrder.current = null;
    }
  }, [selectedCategory]);

  // Scroll to events carousel when "Events" tab is selected
  useEffect(() => {
    if (selectedCategory === "Events" && eventsCarouselRef.current) {
      const el = eventsCarouselRef.current;
      const isMobile = window.innerWidth <= 768;
      const offset = isMobile ? -60 : -80;
      const top = el.getBoundingClientRect().top + window.pageYOffset + offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, [selectedCategory]);

  // Reusable distance fetcher - called immediately when user grants location
  const fetchDistancesForLocation = useCallback(
    async (location) => {
      try {
        hasFetchedDistances.current = true;

        const hasToken = customerToken && viewMode === "resident";
        const distanceMap = await fetchVenueDistances(
          location,
          hasToken ? customerToken : null
        );

        setVenueDistances((prev) => {
          const merged = { ...prev, ...distanceMap };
          setCachedData("bc_venue_distances", merged);
          return merged;
        });
      } catch (err) {
        console.error("Failed to fetch venue distances:", err);
        hasFetchedDistances.current = false;
      }
    },
    [customerToken, viewMode]
  );

  // Request user location permission on first visit (non-blocking)
  useEffect(() => {
    if (!("geolocation" in navigator)) {
      return;
    }

    // If we already have a cached location or know user denied it, don't ask again
    const deniedBefore = sessionStorage.getItem("bc_user_location_denied") === "1";
    if (userLocation || deniedBefore) {
      return;
    }

    // Delay location request to avoid interrupting page load or URL typing
    const locationTimer = setTimeout(() => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          // reset distance flag and store location
          hasFetchedDistances.current = false;
          setUserLocation(location);
          sessionStorage.setItem("bc_user_location", JSON.stringify(location));

          // Immediately trigger backend distance calculation
          fetchDistancesForLocation(location);
        },
        (error) => {
          sessionStorage.setItem("bc_user_location_denied", "1");
          // proceed without location
        }
      );
    }, 2000); // Wait 2 seconds after page load before requesting location

    return () => clearTimeout(locationTimer);
  }, [userLocation, fetchDistancesForLocation]);

  // Check if user is returning from questionnaire after Google signup
  useEffect(() => {
    const returningFromQuestionnaire = sessionStorage.getItem(
      "return_after_questionnaire"
    );
    if (returningFromQuestionnaire) {
      setIsReturningFromQuestionnaire(true);
      sessionStorage.removeItem("return_after_questionnaire");
    }
  }, []);

  // Initialization: load venue data based on auth state
  useEffect(() => {
    const initialize = async () => {
      // Check if we have valid cached data
      const validCache =
        hasValidCache("bc_companies") &&
        hasValidCache("bc_venue_offers");

      // If we have valid cached data and already hydrated, skip loading
      if (validCache && companies.length > 0 && Object.keys(venueOffers).length > 0) {
        setLoading(false);
        setInitialDataReady(true);
        setSortingComplete(true);
        setHasHydrated(true); // Skip hydration delay when using cache
        setIsAuthChanging(false); // Mark auth change as complete

        // Still check enrollments for logged-in users
        if (email && viewMode === "resident" && customerToken) {
          setCheckingEnrollments(true);
        }
        return;
      }

      setIsAuthChanging(true);
      setHasHydrated(false);
      setInitialDataReady(false);

      setLoading(true);
      setSortingComplete(false); // Reset sorting state when initializing

      const redirectVenueId = sessionStorage.getItem("redirect_to_venue");

      if (email && viewMode === "resident" && customerToken) {
        // Logged-in path
        await loadInitialDataForLoggedInUser();

        if (redirectVenueId) {
          sessionStorage.removeItem("redirect_to_venue");
          setPendingViewDealsVenueId(parseInt(redirectVenueId, 10));
        }
      } else {
        // Public path
        await loadInitialData();
      }
    };

    initialize();

    // Cleanup
    return () => {
      isFetchingOffers.current = false;
      isFetchingEnrollments.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, viewMode, customerToken]);

  // Clear coupon counts when user logs out (switches to public mode)
  useEffect(() => {
    if (viewMode === "public" && !email) {
      setCouponCounts({});
    }
  }, [viewMode, email]);

  // Mark initial data as ready once companies and offers are present, or no companies
  useEffect(() => {
    if (initialDataReady) return;

    if (!loading && companies.length === 0) {
      // No venues but loading finished
      setInitialDataReady(true);
    } else if (companies.length > 0 && Object.keys(venueOffers).length > 0) {
      // Ensure ALL venues have their offers loaded before marking as ready
      const allVenuesHaveOffers = companies.every(
        (company) => venueOffers[company.venue_id] !== undefined
      );
      if (allVenuesHaveOffers) {
        // Check if we're using cache - if so, skip animation frames for instant load
        const validCache =
          hasValidCache("bc_companies") &&
          hasValidCache("bc_venue_offers");

        if (validCache) {
          // Instant load from cache
          setInitialDataReady(true);
          setSortingComplete(true);
        } else {
          // Use double requestAnimationFrame to ensure sorting memo completes before marking ready
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setInitialDataReady(true);
              setSortingComplete(true);
            });
          });
        }
      }
    }
  }, [initialDataReady, companies, venueOffers, loading]);

  // Hydrate page only after data ready, sorting complete, and (for residents) enrollment check done
  useEffect(() => {
    if (!hasHydrated && initialDataReady && sortingComplete && !checkingEnrollments) {
      // Check if we're using cache for instant hydration
      const validCache =
        hasValidCache("bc_companies") &&
        hasValidCache("bc_venue_offers");

      if (validCache) {
        // Instant hydration when using cache
        setHasHydrated(true);
        setIsReturningFromQuestionnaire(false);
      } else {
        // Normal hydration with delay for fresh data
        const delay = isReturningFromQuestionnaire ? 500 : 0;

        setTimeout(() => {
          requestAnimationFrame(() => {
            setHasHydrated(true);
            setIsReturningFromQuestionnaire(false);
          });
        }, delay);
      }
    }
  }, [
    hasHydrated,
    initialDataReady,
    sortingComplete,
    checkingEnrollments,
    isReturningFromQuestionnaire,
  ]);

  // Clear auth changing flag after hydration completes
  useEffect(() => {
    if (hasHydrated && isAuthChanging) {
      setIsAuthChanging(false);
    }
  }, [hasHydrated, isAuthChanging]);

  // After we have a hydrated view for this auth state, record it as the snapshot
  useEffect(() => {
    if (hasHydrated && !loading && !checkingEnrollments) {
      setAuthSnapshot({ email, viewMode, customerToken });
    }
  }, [
    hasHydrated,
    loading,
    checkingEnrollments,
    email,
    viewMode,
    customerToken,
  ]);

  // Drive global loading UI
  useEffect(() => {
    setShowLoading(
      !hasHydrated || isAuthChanging || loading || authChangedSinceSnapshot
    );
  }, [
    hasHydrated,
    isAuthChanging,
    loading,
    authChangedSinceSnapshot,
    setShowLoading,
  ]);

  // Initial load for logged-in user (no caching)
  const loadInitialDataForLoggedInUser = async () => {
    await loadCompaniesAndOffers(customerToken || null);
  };

  // Initial load for public user (no caching)
  const loadInitialData = async () => {
    await loadCompaniesAndOffers();
  };

  // Fetch companies, then offers, then set state
  const loadCompaniesAndOffers = async (token = null) => {
    try {
      const { companies: fetchedCompanies, categoryOrder: newCategoryOrder } =
        await fetchEnrolledVenues({ customerToken: token, userLocation });

      if (!token) {
        const {
          email: currentEmail,
          viewMode: currentViewMode,
          customerToken: currentToken,
        } = authStateRef.current;

        if (currentEmail && currentViewMode === "resident" && currentToken) {
          return; // User is logged in now; discard stale public response
        }
      }

      if (fetchedCompanies.length > 0) {
        await loadOffersForVenues(fetchedCompanies, newCategoryOrder);
      } else {
        setCompanies([]);
        setVenueOffers({});
        setCategoryOrder(newCategoryOrder);
        setCachedData("bc_category_order", newCategoryOrder);
        setCheckingEnrollments(false);
        setLoading(false);
      }
    } catch (error) {
      console.error("Failed to fetch companies:", error);
      setCompanies([]);
      setVenueOffers({});
      setCategoryOrder([]);
      sessionStorage.removeItem("bc_category_order");
      setCheckingEnrollments(false);
      setLoading(false);
      setInitialDataReady(true);
      setSortingComplete(true);
    }
  };

  // Fetch offers for all venues, then set state and trigger enrollment checks if needed
  const loadOffersForVenues = async (companiesList, newCategoryOrder) => {
    if (isFetchingOffers.current) return;
    isFetchingOffers.current = true;

    try {
      const offersData = await fetchAllVenueOffers(companiesList);

      setCompanies(companiesList);
      setVenueOffers(offersData);
      setCategoryOrder(newCategoryOrder);

      // Cache all data
      setCachedData("bc_companies", companiesList);
      setCachedData("bc_venue_offers", offersData);
      setCachedData("bc_category_order", newCategoryOrder);

      if (email) {
        setCheckingEnrollments(true);
      } else {
        setCheckingEnrollments(false);
      }

      setLoading(false);
    } finally {
      isFetchingOffers.current = false;
    }
  };

  // Fallback distance effect - for cached locations or late venue loads
  useEffect(() => {
    if (!userLocation) return;
    if (companies.length === 0) return;
    if (hasFetchedDistances.current) return;

    // Fallback path – e.g. returning visit with cached location
    fetchDistancesForLocation(userLocation);
  }, [userLocation, companies, fetchDistancesForLocation]);

  const loadEnrollmentStatus = async (userEmail) => {
    if (isFetchingEnrollments.current) return;
    isFetchingEnrollments.current = true;
    setCheckingEnrollments(true);

    try {
      const statusMap = await checkAllEnrollments(companies, userEmail);
      setEnrollmentStatus(statusMap);
      setCheckingEnrollments(false);
    } finally {
      isFetchingEnrollments.current = false;
    }
  };

  // Load customer coupon counts and update expiration data
  const loadCouponCounts = async (token) => {
    try {
      const { counts, expirations } = await fetchCouponCounts(token);

      setCouponCounts(counts);

      // Update companies with expiration data
      setCompanies(prev => prev.map(company => ({
        ...company,
        min_expiration_days: expirations[company.venue_id] || null
      })));
    } catch (error) {
      console.error("Failed to load coupon counts:", error);
      setCouponCounts({});
    }
  };

  // Load trending events + their ticket prices together so cards render with price immediately
  const loadTrendingEvents = async () => {
    try {
      const events = await fetchTrendingEvents(10);
      setTrendingEvents(events);
      setCachedData("bc_trending_events", events);

      // Fetch prices for all events in parallel so EventCard doesn't need its own fetch
      const token = authStateRef.current.customerToken;
      const results = await Promise.allSettled(
        events.map((e) =>
          getEventTiers(e.event_id, token).then((tiers) => ({
            event_id: e.event_id,
            price: getLowestDisplayPrice(tiers),
          }))
        )
      );
      const priceMap = {};
      results.forEach((r) => {
        if (r.status === "fulfilled") priceMap[r.value.event_id] = r.value.price;
      });
      setEventPrices(priceMap);
      setCachedData("bc_event_prices", priceMap);
    } catch (error) {
      console.error("Failed to load trending events:", error);
      setTrendingEvents([]);
    }
  };

  // Fetch trending events on mount - only if cache is invalid
  useEffect(() => {
    if (!hasValidCache("bc_trending_events") || trendingEvents.length === 0) {
      loadTrendingEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When companies loaded and user is resident, run enrollment checks
  useEffect(() => {
    if (
      companies.length > 0 &&
      email &&
      viewMode === "resident" &&
      checkingEnrollments
    ) {
      loadEnrollmentStatus(email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, email, viewMode, checkingEnrollments]);

  // Fetch coupon counts when user is logged in with customer token
  useEffect(() => {
    if (companies.length > 0 && customerToken && viewMode === "resident") {
      loadCouponCounts(customerToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, customerToken, viewMode]);

  const handleViewDeals = useCallback(
    async (venueId) => {
      if (viewMode === "public" || !email) {
        navigate(`/coupons/public/venue/${venueId}`);
        return;
      }

      setLoadingEnrollment((prev) => ({ ...prev, [venueId]: true }));
      try {
        const token = await getCustomerToken(email);
        navigate(`/coupons/${token}/venue/${venueId}`);
      } catch (error) {
        alert(error.message || "Failed to load deals. Please try again.");
      } finally {
        setLoadingEnrollment((prev) => ({ ...prev, [venueId]: false }));
      }
    },
    [viewMode, email, navigate]
  );

  const handleEventClick = (eventSlug) => {
    navigate(`/events/${eventSlug}`);
  };

  // After everything is ready, and if we had a pending redirect, navigate
  useEffect(() => {
    if (
      pendingViewDealsVenueId &&
      hasHydrated &&
      !loading &&
      !checkingEnrollments &&
      email &&
      viewMode === "resident"
    ) {
      const venueId = pendingViewDealsVenueId;
      setPendingViewDealsVenueId(null);
      handleViewDeals(venueId);
    }
  }, [
    pendingViewDealsVenueId,
    hasHydrated,
    loading,
    checkingEnrollments,
    email,
    viewMode,
    handleViewDeals,
  ]);



  const categories = useMemo(() => [
    "All",
    "Events",
    ...new Set(
      companies.map((c) => c.category || "Restaurant").filter(Boolean)
    ),
  ], [companies]);

  const eventsCarouselRef = useRef(null);

  // Fetch category icons from backend with caching
  const [categoryIconMap, setCategoryIconMap] = useState(() =>
    initializeWithCache("bc_category_icons", {})
  );

  useEffect(() => {
    if (!hasValidCache("bc_category_icons") || Object.keys(categoryIconMap).length === 0) {
      const loadCategoryIcons = async () => {
        try {
          const iconMap = await fetchCategoryIcons();
          setCategoryIconMap(iconMap);
          setCachedData("bc_category_icons", iconMap);
        } catch (error) {
          console.warn("Failed to load category icons:", error);
        }
      };

      loadCategoryIcons();
    }
  }, [categoryIconMap]);

  const filteredCompanies = companies.filter((company) => {
    const matchesCategory =
      selectedCategory === "All" ||
      selectedCategory === "Events" ||
      (company.category || "Restaurant") === selectedCategory;

    return matchesCategory;
  });

  // Update header context with categories data
  useEffect(() => {
    updateHeaderData({
      categories,
      selectedCategory,
      onCategoryChange: setSelectedCategory,
      categoryIconMap,
      userLocation,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, selectedCategory, categoryIconMap, userLocation, updateHeaderData]);

  // Group and sort by coupon availability before rendering
  const { sortedGroupedVenues, orderedCategories } = useMemo(() => {
    const groupedByCategory = filteredCompanies.reduce((acc, company) => {
      const category = company.category || "Restaurant";
      if (!acc[category]) acc[category] = [];
      acc[category].push(company);
      return acc;
    }, {});

    Object.keys(groupedByCategory).forEach((categoryName) => {
      groupedByCategory[categoryName].sort((a, b) => {
        const aHasCoupons = venueOffers[a.venue_id]?.length > 0 ? 1 : 0;
        const bHasCoupons = venueOffers[b.venue_id]?.length > 0 ? 1 : 0;

        if (aHasCoupons !== bHasCoupons) {
          return bHasCoupons - aHasCoupons;
        }
        return 0;
      });
    });

    // Lock in the category order on FIRST calculation and NEVER change it
    if (!finalizedCategoryOrder.current) {
      let cats;
      if (categoryOrder.length > 0) {
        const categoryOrderNames = categoryOrder.map((cat) => cat.category);
        cats = categoryOrderNames.filter((catName) => groupedByCategory[catName] && groupedByCategory[catName].length > 0);
      } else {
        cats = Object.keys(groupedByCategory).sort();
      }

      // Lock it in immediately - this order will NEVER change
      if (cats.length > 0) {
        finalizedCategoryOrder.current = cats;
      }
    }

    return {
      sortedGroupedVenues: groupedByCategory,
      orderedCategories: finalizedCategoryOrder.current || [],
    };
  }, [filteredCompanies, venueOffers, categoryOrder]);

  // FINAL: single place where we decide to show LoadingPage
  if (!hasHydrated || isAuthChanging || loading || authChangedSinceSnapshot) {
    return <LoadingPage />;
  }

  return (
    <div className="perks-directory-container">
      {/* Light strip under header for subtle visual separation */}
      <div className="perks-hero-strip"></div>

      <div className="perks-content-wrapper">
        {/* Hero Section - Only shown when no filter is active */}
        {selectedCategory === "All" && (
          <div className="perks-hero-section">
            <h1 className="perks-hero-main-title">Community Perks & Events!</h1>
            <button
              className="perks-how-it-works-link"
              onClick={() => {
                const section = document.getElementById('how-it-works-section');
                if (section) {
                  const isMobile = window.innerWidth <= 768;
                  const offset = isMobile ? -40 : 0; // Scroll up slightly on mobile to show content above section
                  const elementPosition = section.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset + offset;

                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              }}
            >
              Learn how it works ↓
            </button>
          </div>
        )}

        {companies.length > 0 && selectedCategory !== "All" && selectedCategory !== "Events" && (
          <div className="perks-search-section">
            <div className="perks-category-title-header">
              <h2>{t(`categories.${selectedCategory}`, selectedCategory)} near me</h2>
            </div>
            <div className="perks-search-results">
              <p>
                Found <strong>{filteredCompanies.length}</strong> {filteredCompanies.length === 1 ? 'venue' : 'venues'} in <strong>{t(`categories.${selectedCategory}`, selectedCategory)}</strong>
              </p>
            </div>
          </div>
        )}

        {/* Show trending events at top when no filter active or Events tab selected */}
        {(selectedCategory === "All" || selectedCategory === "Events") && (
          <div ref={eventsCarouselRef}>
            <EventsCarousel
              events={trendingEvents}
              onEventClick={handleEventClick}
              hasHydrated={hasHydrated}
              eventPrices={eventPrices}
            />
          </div>
        )}

        <VenuesList
          companies={companies}
          filteredCompanies={filteredCompanies}
          sortedGroupedVenues={sortedGroupedVenues}
          orderedCategories={orderedCategories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          venueDistances={venueDistances}
          venueOffers={venueOffers}
          enrollmentStatus={enrollmentStatus}
          loadingEnrollment={loadingEnrollment}
          couponCounts={couponCounts}
          handleViewDeals={handleViewDeals}
          categoryIconMap={categoryIconMap}
          hasHydrated={hasHydrated}
        />

        {/* Show trending events at bottom when a venue category filter is active */}
        {selectedCategory !== "All" && selectedCategory !== "Events" && (
          <EventsCarousel
            events={trendingEvents}
            onEventClick={handleEventClick}
            hasHydrated={hasHydrated}
            eventPrices={eventPrices}
          />
        )}

        <HowItWorksSection />
      </div>
    </div>
  );
}

export default LoyaltyDirectory;
